import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE as API, MANAGE_URL } from "../config/constants";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { downloadPDF } from "../utils/exportHelpers";
import {
  FaDownload,
  FaEye,
  FaLink,
  FaSyncAlt,
  FaUnlink,
  FaSearch,
  FaTrash,
  FaCheckSquare,
  FaRegSquare,
  FaPlus,
} from "react-icons/fa";

const COLOR = {
  primary: "var(--app-primary)",
  bg: "#f5f7fb",
  line: "#e5e7eb",
  card: "#fff",
  text: "#0f172a",
  muted: "#6b7280",
  green: "#16a34a",
  yellow: "#b45309",
  red: "#dc2626",
};

const SUPPORT_WHATSAPP = "919988777409";

const normalizeTableKey = (value) => String(value ?? "").trim();
const normalizeLookupKey = (value) => normalizeTableKey(value).toLowerCase();
const isTakeawayKey = (value) => {
  const raw = normalizeTableKey(value).toLowerCase();
  return raw === "0" || raw === "-1" || raw.startsWith("takeaway");
};

const getTableKey = (table) =>
  normalizeTableKey(
    table?.table_key ??
      table?.tableKey ??
      table?.key ??
      table?.name ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      ""
  );

const getTableDisplayName = (table) =>
  normalizeTableKey(
    table?.display_name ??
      table?.displayName ??
      table?.name ??
      table?.table ??
      table?.table_key ??
      table?.tableKey ??
      ""
  );

const getTableType = (table) => {
  const raw = String(table?.table_type ?? table?.tableType ?? table?.type ?? "table").toLowerCase();
  return raw === "room" ? "room" : "table";
};

const buildTableMetaMap = (list = []) => {
  const map = {};
  (list || []).forEach((entry) => {
    const key = getTableKey(entry);
    if (!key) return;
    map[key] = {
      type: getTableType(entry),
      displayName: getTableDisplayName(entry) || key,
    };
  });
  return map;
};

const formatTableWithPrefix = (value, tableMetaByKey) => {
  const raw = normalizeTableKey(value);
  if (!raw) return "Not assigned";
  if (isTakeawayKey(raw)) {
    const match = raw.toLowerCase().match(/takeaway[-\s]*(\d+)/);
    return `Takeaway ${match ? match[1] : ""}`.trim();
  }
  const meta = tableMetaByKey[raw];
  if (!meta) return `Table ${raw}`;
  const prefix = meta.type === "room" ? "Room" : "Table";
  const name = String(meta.displayName || raw).trim();
  if (!name) return prefix;
  if (name.toLowerCase().startsWith(prefix.toLowerCase())) return name;
  return `${prefix} ${name}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result || "");
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const QRManagementPage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [userId, setUserId] = useState(null);
  const [items, setItems] = useState([]);
  const [tableList, setTableList] = useState([]);
  const [tableMetaByKey, setTableMetaByKey] = useState({});
  const [pendingOrders, setPendingOrders] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [entitlement, setEntitlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [submittingId, setSubmittingId] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningQr, setAssigningQr] = useState(null);
  const [assigningTableKey, setAssigningTableKey] = useState("");
  const [provisionModalOpen, setProvisionModalOpen] = useState(false);
  const [requestedQrCount, setRequestedQrCount] = useState("1");
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedQrIds, setSelectedQrIds] = useState([]);
  const previewRef = useRef(null);

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem("userData") || "{}");
    const resolved = data?.data || data;
    if (!resolved?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserData(resolved);
    setUserId(resolved.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    refreshAll({ initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const normalizeList = (json) => {
    const arr = Array.isArray(json?.QRCodeList) ? json.QRCodeList : [];
    return arr.map((it, idx) => {
      const serial = it?.serial_number ?? it?.serial ?? it?.code ?? it?.qrcode_serial ?? "";
      const tableKey = normalizeTableKey(it?.table_number ?? it?.table_key ?? "");
      return {
        ...it,
        id: it?.id ?? idx + 1,
        serial: String(serial || "").trim(),
        tableKey,
        attached: Number(it?.is_attached) !== 0 && Boolean(tableKey),
        attachmentId: it?.attachment_id ?? null,
        image: it?.qr_image || it?.image || null,
      };
    });
  };

  const buildManageUrl = (row) => {
    const code = row?.serial;
    if (!code) return MANAGE_URL;
    return `${MANAGE_URL}?code=${encodeURIComponent(code)}`;
  };

  const fetchList = async (rid = userId) => {
    const res = await fetch(`${API}/myqrcodelist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: rid }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.status || json?.message || "Failed to load QR list");
    const normalized = normalizeList(json);
    setItems(normalized);
  };

  const fetchPendingOrders = async (rid = userId) => {
    setPendingLoading(true);
    try {
      const d = new Date();
      const pad = (v) => String(v).padStart(2, "0");
      const today = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      const res = await fetch(`${API}/list/orderfromQR`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: rid, fromdate: today, todate: today, type: -1 }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.status || json?.message || "Failed to load pending QR orders");
      setPendingOrders(Array.isArray(json?.list) ? json.list : []);
    } catch (e) {
      toast.error(e.message || "Failed to load pending QR orders");
    } finally {
      setPendingLoading(false);
    }
  };

  const fetchEntitlement = async (rid = userId) => {
    const res = await fetch(`${API}/qr/entitlement-status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: rid }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || "Failed to load QR plan status");
    setEntitlement(json);
  };

  const fetchTables = async (rid = userId) => {
    const res = await fetch(`${API}/list/table`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: rid }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || "Failed to load tables");
    const list = Array.isArray(json?.list) ? json.list : [];
    setTableList(list);
    setTableMetaByKey(buildTableMetaMap(list));
  };

  const refreshAll = async ({ initial = false } = {}) => {
    if (!userId) return;
    initial ? setLoading(true) : setRefreshing(true);
    try {
      await Promise.all([
        fetchList(userId),
        fetchPendingOrders(userId),
        fetchEntitlement(userId),
        fetchTables(userId),
      ]);
    } catch (e) {
      toast.error(e.message || "Unable to refresh QR management");
    } finally {
      initial ? setLoading(false) : setRefreshing(false);
    }
  };

  const hasAssignedTable = (row) => Boolean(normalizeTableKey(row?.tableKey || row?.table_number));

  const openPreview = async (row) => {
    if (!hasAssignedTable(row)) {
      toast.info("Assign table first to preview QR");
      return;
    }
    const url = buildManageUrl(row);
    setSelected(row);
    setTargetUrl(url);

    const qrSources = [
      `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`,
      `https://quickchart.io/qr?size=220&text=${encodeURIComponent(url)}`,
      row?.image || "",
    ].filter(Boolean);

    setPreviewUrl("");
    try {
      let dataUrl = "";
      for (const src of qrSources) {
        try {
          const res = await fetch(src, { mode: "cors" });
          if (!res.ok) continue;
          const blob = await res.blob();
          dataUrl = await blobToDataUrl(blob);
          if (String(dataUrl || "").startsWith("data:image")) break;
        } catch {
          // try next source
        }
      }

      if (!dataUrl) {
        toast.error("Unable to load QR image right now. Please retry.");
        return;
      }

      setPreviewUrl(dataUrl);
    } catch (e) {
      toast.error(e?.message || "Unable to load QR image");
    }
  };

  const handleDetach = async (row) => {
    if (!row?.attached) return;
    try {
      setSubmittingId(row.id);
      setItems((prev) =>
        prev.map((it) =>
          it.id === row.id
            ? { ...it, attached: false, attachmentId: null, tableKey: "", table_number: null }
            : it
        )
      );
      const res = await fetch(`${API}/deattach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, qrcodeid: row.id }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.status || json?.message || "Detach failed");
      }
      toast.success("Detached successfully");
      await Promise.all([fetchList(userId), fetchEntitlement(userId)]);
    } catch (e) {
      toast.error(e.message || "Failed to detach");
      setItems((prev) => prev.map((it) => (it.id === row.id ? { ...it, attached: true } : it)));
    } finally {
      setSubmittingId(null);
    }
  };

  const openAssignModal = (row) => {
    setAssigningQr(row);
    setAssigningTableKey("");
    setAssignModalOpen(true);
  };

  const handleAssignTable = async () => {
    if (!assigningQr?.id || !assigningTableKey) return;
    try {
      setSubmittingId(assigningQr.id);
      const res = await fetch(`${API}/qr/assign-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          restaurantid: userId,
          qrcodeid: assigningQr.id,
          table_key: assigningTableKey,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Failed to assign table");
      setAssignModalOpen(false);
      setAssigningQr(null);
      setAssigningTableKey("");
      toast.success("Table assigned");
      await Promise.all([fetchList(userId), fetchEntitlement(userId)]);
    } catch (e) {
      toast.error(e.message || "Failed to assign table");
    } finally {
      setSubmittingId(null);
    }
  };

  const deleteQrIds = async (ids = []) => {
    const validIds = ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    if (!validIds.length) return;
    const res = await fetch(`${API}/qr/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ restaurantid: userId, qrcodeids: validIds }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.message || "Failed to delete QR");
    return json;
  };

  const handleDeleteSingle = async (row) => {
    if (!row?.id) return;
    if (row.attached || hasAssignedTable(row)) {
      toast.info("Detach QR from table before deleting");
      return;
    }
    if (!window.confirm("Delete this QR code?")) return;
    try {
      setSubmittingId(row.id);
      const result = await deleteQrIds([row.id]);
      toast.success(result?.message || "QR deleted");
      await Promise.all([fetchList(userId), fetchEntitlement(userId)]);
    } catch (e) {
      toast.error(e.message || "Failed to delete QR");
    } finally {
      setSubmittingId(null);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedQrIds.length) return;
    const selectedRows = items.filter((i) => selectedQrIds.includes(i.id));
    if (selectedRows.some((row) => row.attached || hasAssignedTable(row))) {
      toast.info("Detach selected QRs before deleting");
      return;
    }
    if (!window.confirm(`Delete ${selectedQrIds.length} selected QR codes?`)) return;
    try {
      setSubmittingId("bulk");
      const result = await deleteQrIds(selectedQrIds);
      toast.success(result?.message || "Selected QRs deleted");
      setSelectedQrIds([]);
      setSelectionMode(false);
      await Promise.all([fetchList(userId), fetchEntitlement(userId)]);
    } catch (e) {
      toast.error(e.message || "Failed to delete selected QRs");
    } finally {
      setSubmittingId(null);
    }
  };

  const openProvisionModal = () => {
    const remaining = Number(entitlement?.remaining ?? 0);
    if (remaining <= 0 && !entitlement?.can_auto_add) {
      requestGenerateOnWhatsApp();
      return;
    }
    setRequestedQrCount(remaining > 0 ? "1" : "0");
    setProvisionModalOpen(true);
  };

  const requestGenerateOnWhatsApp = (requestedExtra = null) => {
    const registeredMobile = String(userData?.mobile_number || userData?.phone || "").trim();
    const packageType = String(entitlement?.package_type || userData?.package_type || "").trim();
    const maxAllowed = Number(entitlement?.max_allowed ?? 0);
    const assigned = Number(entitlement?.assigned_count ?? items.length);
    const msg = encodeURIComponent(
      `Hi Bill Sarthi Team,\n\nI want additional QR codes.\n\nRegistered Mobile: ${registeredMobile || "-"}\nRestaurant ID: ${userId || "-"}\nAssigned QRs: ${assigned}\nPlan QR limit: ${maxAllowed}\nCurrent plan: ${packageType || "-"}${requestedExtra ? `\nRequested extra QR: ${requestedExtra}` : ""}`
    );
    const link = `https://wa.me/${SUPPORT_WHATSAPP}?text=${msg}`;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleAutoProvision = async () => {
    const count = Number.parseInt(String(requestedQrCount || "").trim(), 10);
    const remaining = Number(entitlement?.remaining ?? 0);
    if (!Number.isFinite(count) || count <= 0) {
      toast.error("Enter valid QR count");
      return;
    }
    if (count > remaining) {
      toast.info(`Max allowed now: ${remaining}`);
      requestGenerateOnWhatsApp(count - remaining);
      return;
    }
    try {
      setIsProvisioning(true);
      const res = await fetch(`${API}/qr/auto-provision`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: userId, count }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Failed to add QR");
      toast.success(json?.message || "QR added");
      setProvisionModalOpen(false);
      await Promise.all([fetchList(userId), fetchEntitlement(userId)]);
    } catch (e) {
      toast.error(e.message || "Failed to auto-add QR");
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleDownloadCard = () => {
    if (!previewRef.current || !selected) return;
    if (!previewUrl) {
      toast.info("QR image still loading. Please wait and try again.");
      return;
    }
    const nameBase = selected.serial || `qr_${selected.id || "code"}`;
    const safeName = String(nameBase).replace(/[^a-z0-9_-]/gi, "_");
    downloadPDF(previewRef.current, `${safeName}.pdf`);
  };

  const toggleSelection = (id) => {
    setSelectedQrIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = (rows) => {
    const ids = rows.map((x) => x.id);
    if (ids.length && ids.every((id) => selectedQrIds.includes(id))) {
      setSelectedQrIds((prev) => prev.filter((id) => !ids.includes(id)));
      return;
    }
    setSelectedQrIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        String(it.serial || "").toLowerCase().includes(q) ||
        String(it.id || "").includes(q) ||
        String(it.tableKey || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const stats = useMemo(() => {
    const total = items.length;
    const attached = items.filter((i) => i.attached).length;
    return { total, attached, free: total - attached };
  }, [items]);

  const assignedTableLookupSet = useMemo(() => {
    const set = new Set();
    items.forEach((row) => {
      if (!hasAssignedTable(row)) return;
      if (assigningQr?.id && Number(row.id) === Number(assigningQr.id)) return;
      const lookup = normalizeLookupKey(row.tableKey || row.table_number);
      if (lookup) set.add(lookup);
    });
    return set;
  }, [items, assigningQr]);

  if (loading) {
    return (
      <Centered>
        <ActivityIndicatorCircle />
        <div>Loading QR codes…</div>
      </Centered>
    );
  }

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2500} />
      <TopBar>
        <TopLeft>
          <Title>QR Management</Title>
          <Sub>Assign table, preview, detach and manage your QR inventory</Sub>
        </TopLeft>
        <TopActions>
          <SearchBox>
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by serial, ID, table"
            />
          </SearchBox>
          <PrimaryBtn onClick={() => refreshAll()}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
          <GhostBtn onClick={() => setSelectionMode((s) => !s)}>
            {selectionMode ? <FaRegSquare /> : <FaCheckSquare />} {selectionMode ? "Cancel Select" : "Select"}
          </GhostBtn>
        </TopActions>
      </TopBar>

      <PendingCard>
        <PrimaryBtn onClick={() => navigate("/qr/table-order")}>View Pending Orders ({pendingLoading ? "..." : pendingOrders.length})</PrimaryBtn>
      </PendingCard>

      <PlanCard>
        <PlanTop>
          <div>
            <CardTitle>QR Plan Status</CardTitle>
            <CardSub>
              Package: {String(entitlement?.package_type || userData?.package_type || "-")}
            </CardSub>
          </div>
          <PlanCount>
            {Number(entitlement?.assigned_count ?? items.length)} / {Number(entitlement?.max_allowed ?? 0)}
          </PlanCount>
        </PlanTop>
        <PlanActions>
          <PrimaryBtn onClick={openProvisionModal} disabled={isProvisioning}>
            <FaPlus /> {isProvisioning ? "Adding..." : "Auto Add QR"}
          </PrimaryBtn>
          <GhostBtn onClick={() => requestGenerateOnWhatsApp()}>Request Support</GhostBtn>
        </PlanActions>
      </PlanCard>

      <StatRow>
        <StatCard>
          <StatLabel>Total QR</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard $green>
          <StatLabel>Attached</StatLabel>
          <StatValue>{stats.attached}</StatValue>
        </StatCard>
        <StatCard $amber>
          <StatLabel>Free</StatLabel>
          <StatValue>{stats.free}</StatValue>
        </StatCard>
      </StatRow>

      {selectionMode && filtered.length > 0 ? (
        <BulkBar>
          <span>Selected: {selectedQrIds.length}</span>
          <GhostBtn onClick={() => toggleSelectAll(filtered)}>
            {filtered.every((r) => selectedQrIds.includes(r.id)) ? "Clear All" : "Select All"}
          </GhostBtn>
          <DangerBtn disabled={!selectedQrIds.length || submittingId === "bulk"} onClick={handleDeleteSelected}>
            <FaTrash /> Delete Selected ({selectedQrIds.length})
          </DangerBtn>
        </BulkBar>
      ) : null}

      <Grid>
        {filtered.map((row) => {
          const isSelected = selectedQrIds.includes(row.id);
          const assignedText = formatTableWithPrefix(row.tableKey || row.table_number, tableMetaByKey);
          const canDelete = !row.attached && !hasAssignedTable(row);
          return (
            <Card
              key={row.id}
              $attached={row.attached}
              $selected={selectionMode && isSelected}
              onClick={() => (selectionMode ? toggleSelection(row.id) : openPreview(row))}
            >
              <CardTop>
                <div>
                  <CardTitle>QR #{row.id}</CardTitle>
                  <CardSub>Serial: {row.serial || "-"}</CardSub>
                  <CardMeta>Assigned To: {assignedText}</CardMeta>
                </div>
                <Thumb $attached={row.attached}>
                  {row.image ? <img src={row.image} alt="qr" /> : <ThumbStub>QR</ThumbStub>}
                </Thumb>
              </CardTop>

              <CardBottom>
                <Status $attached={row.attached}>{row.attached ? "Attached" : "Detached"}</Status>
                <ActionRow>
                  {!selectionMode ? (
                    <CardActionBtn
                      $variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPreview(row);
                      }}
                    >
                      <FaEye /> Preview
                    </CardActionBtn>
                  ) : null}

                  {row.attached ? (
                    <CardActionBtn
                      $variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!window.confirm("Detach this QR from table?")) return;
                        handleDetach(row);
                      }}
                      disabled={submittingId === row.id}
                    >
                      <FaUnlink /> {submittingId === row.id ? "Detaching..." : "Detach"}
                    </CardActionBtn>
                  ) : (
                    <CardActionBtn
                      $variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAssignModal(row);
                      }}
                      disabled={submittingId === row.id}
                    >
                      <FaLink /> Assign
                    </CardActionBtn>
                  )}

                  <CardActionBtn
                    $variant={canDelete ? "danger" : "disabled"}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSingle(row);
                    }}
                    disabled={!canDelete || submittingId === row.id}
                  >
                    <FaTrash /> Delete
                  </CardActionBtn>
                </ActionRow>
              </CardBottom>
            </Card>
          );
        })}
        {filtered.length === 0 && <Empty>No QR codes found for this search.</Empty>}
      </Grid>

      {selected && (
        <Modal onMouseDown={(e) => e.target === e.currentTarget && setSelected(null)}>
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <ModalTitle>QR PREVIEW</ModalTitle>
                <ModalSub>{selected.serial || `QR #${selected.id}`}</ModalSub>
              </div>
              <CloseBtn onClick={() => setSelected(null)}>✕</CloseBtn>
            </ModalHead>

            <PreviewCard ref={previewRef}>
              <PreviewTop>
                <PreviewTitle>SCAN & ORDER</PreviewTitle>
                <QrBox>
                  {previewUrl ? (
                    <img src={previewUrl} alt="qr" crossOrigin="anonymous" />
                  ) : (
                    <ActivityIndicatorCircle />
                  )}
                </QrBox>
                <CodePill>{selected.serial || `QR#${selected.id}`}</CodePill>
                <PreviewSub>
                  Assigned To: {formatTableWithPrefix(selected.tableKey || selected.table_number, tableMetaByKey)}
                </PreviewSub>
              </PreviewTop>
              <PreviewBottom>
                <img
                  src="https://billsarthi.com/wp-content/uploads/2025/03/logo-white-1.png"
                  alt="BillSarthi"
                />
                <span>Powered by BillSarthi</span>
              </PreviewBottom>
            </PreviewCard>

            <ModalActions>
              <PrimaryBtn onClick={handleDownloadCard}>
                <FaDownload /> Download
              </PrimaryBtn>
              <GhostBtn as="a" href={targetUrl} target="_blank" rel="noreferrer">
                <FaLink /> Open URL
              </GhostBtn>
            </ModalActions>
          </ModalCard>
        </Modal>
      )}

      {assignModalOpen ? (
        <Modal onMouseDown={(e) => e.target === e.currentTarget && setAssignModalOpen(false)}>
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalTitle>Select Table</ModalTitle>
            <ModalSub>{assigningQr?.serial ? `Serial: ${assigningQr.serial}` : "Choose table"}</ModalSub>
            <AssignList>
              {!tableList.length ? (
                <EmptySmall>No tables found</EmptySmall>
              ) : (
                tableList.map((table, idx) => {
                  const key = normalizeTableKey(getTableKey(table) || getTableDisplayName(table));
                  const label = formatTableWithPrefix(key, tableMetaByKey);
                  const active = assigningTableKey === key;
                  const isAssignedElsewhere = assignedTableLookupSet.has(normalizeLookupKey(key));
                  return (
                    <AssignRow
                      key={key || String(table?.id || idx)}
                      $active={active}
                      $disabled={isAssignedElsewhere}
                      onClick={() => {
                        if (isAssignedElsewhere) return;
                        setAssigningTableKey(key);
                      }}
                    >
                      <span>{label}</span>
                      {isAssignedElsewhere ? <SmallPill>Already Assigned</SmallPill> : active ? "✓" : ""}
                    </AssignRow>
                  );
                })
              )}
            </AssignList>
            <ModalActions>
              <GhostBtn onClick={() => setAssignModalOpen(false)}>Cancel</GhostBtn>
              <PrimaryBtn onClick={handleAssignTable} disabled={!assigningTableKey || submittingId === assigningQr?.id}>
                Assign Now
              </PrimaryBtn>
            </ModalActions>
          </ModalCard>
        </Modal>
      ) : null}

      {provisionModalOpen ? (
        <Modal onMouseDown={(e) => e.target === e.currentTarget && setProvisionModalOpen(false)}>
          <ModalCard onMouseDown={(e) => e.stopPropagation()}>
            <ModalTitle>Enter QR Count</ModalTitle>
            <ModalSub>
              You can add up to {Number(entitlement?.remaining ?? 0)} QR right now.
            </ModalSub>
            <Input
              value={requestedQrCount}
              onChange={(e) => setRequestedQrCount(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="1"
            />
            <ModalActions>
              <GhostBtn onClick={() => setProvisionModalOpen(false)}>Cancel</GhostBtn>
              <PrimaryBtn onClick={handleAutoProvision} disabled={isProvisioning}>
                {isProvisioning ? "Adding..." : "Add Now"}
              </PrimaryBtn>
            </ModalActions>
          </ModalCard>
        </Modal>
      ) : null}
    </Page>
  );
};

export default QRManagementPage;

const Page = styled.div`
  background: radial-gradient(circle at 10% 10%, rgba(var(--app-primary-rgb), 0.08), transparent 45%), ${COLOR.bg};
  min-height: 100vh;
  padding: 18px;
`;
const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
`;
const TopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
const Title = styled.h1`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 900;
  color: ${COLOR.text};
`;
const Sub = styled.div`
  color: ${COLOR.muted};
  font-size: 0.82rem;
`;
const TopActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;
const SearchBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.04);
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
    min-width: 220px;
  }
`;
const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 14px;
  border: none;
  border-radius: 12px;
  background: ${COLOR.primary};
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(var(--app-primary-rgb), 0.2);
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid ${COLOR.primary};
  color: ${COLOR.primary};
  background: var(--app-primary-soft);
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
`;
const DangerBtn = styled(PrimaryBtn)`
  background: ${COLOR.red};
  box-shadow: 0 10px 24px rgba(220, 38, 38, 0.2);
`;

const PendingCard = styled.div`
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
`;

const PlanCard = styled.div`
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
  margin-bottom: 12px;
`;
const PlanTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
`;
const PlanCount = styled.div`
  font-weight: 900;
  font-size: 1rem;
  color: ${COLOR.text};
`;
const PlanActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin: 12px 0 16px;
`;
const StatCard = styled.div`
  background: ${({ $green, $amber }) => ($green ? "#ecfdf3" : $amber ? "#fff7ed" : "var(--app-primary-soft)")};
  border: 1px solid ${({ $green, $amber }) => ($green ? "#bbf7d0" : $amber ? "#fed7aa" : "#cfe6ff")};
  border-radius: 16px;
  padding: 12px 14px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
`;
const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${COLOR.muted};
`;
const StatValue = styled.div`
  font-size: 1.1rem;
  font-weight: 900;
  color: ${COLOR.text};
  margin-top: 4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
`;
const Card = styled.div`
  background: ${COLOR.card};
  border: ${({ $selected }) => ($selected ? "2.5px solid #22c55e" : "1px solid #dbe3ef")};
  border-radius: 16px;
  padding: 16px;
  box-shadow: ${({ $selected }) =>
    $selected
      ? "0 0 0 3px rgba(34, 197, 94, 0.18), 0 10px 24px rgba(15, 23, 42, 0.1)"
      : "0 8px 18px rgba(15, 23, 42, 0.06)"};
  opacity: ${({ $attached }) => ($attached ? 1 : 0.95)};
  cursor: pointer;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: #c7d6ea;
    box-shadow: 0 14px 28px rgba(15, 23, 42, 0.1);
    transform: translateY(-1px);
  }
`;
const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
`;
const CardTitle = styled.div`
  font-weight: 900;
  font-size: 0.9rem;
  color: ${COLOR.text};
  letter-spacing: -0.2px;
`;
const CardSub = styled.div`
  color: #667085;
  font-size: 0.72rem;
  margin-top: 4px;
`;
const CardMeta = styled.div`
  color: #1f2937;
  font-size: 0.82rem;
  margin-top: 8px;
  font-weight: 800;
`;
const Thumb = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 18px;
  background: ${({ $attached }) => ($attached ? "#eaf1fb" : "#f7f9fc")};
  border: 1px solid #d6e0ec;
  display: grid;
  place-items: center;
  overflow: hidden;
  flex-shrink: 0;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;
const ThumbStub = styled.div`
  font-size: 1.7rem;
  font-weight: 900;
  color: ${COLOR.primary};
`;
const CardBottom = styled.div`
  display: grid;
  gap: 10px;
  margin-top: 14px;
`;
const Status = styled.span`
  padding: 5px 10px;
  border-radius: 999px;
  font-weight: 800;
  width: fit-content;
  background: ${({ $attached }) => ($attached ? "#ecfdf3" : "#f8fafc")};
  color: ${({ $attached }) => ($attached ? "#166534" : "#475569")};
  border: 1px solid ${({ $attached }) => ($attached ? "#86efac" : COLOR.line)};
  font-size: 0.78rem;
`;
const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  @media (max-width: 460px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const CardActionBtn = styled.button`
  min-height: 36px;
  border-radius: 12px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 800;
  font-size: 0.84rem;
  cursor: pointer;
  padding: 0 8px;
  background: ${({ $variant }) =>
    $variant === "primary"
      ? COLOR.primary
      : $variant === "danger"
      ? "#fff1f2"
      : $variant === "disabled"
      ? "#f3f4f6"
      : "#f6f9fc"};
  color: ${({ $variant }) =>
    $variant === "primary"
      ? "#fff"
      : $variant === "danger"
      ? "#b42318"
      : $variant === "disabled"
      ? "#98a2b3"
      : COLOR.primary};
  border-color: ${({ $variant }) =>
    $variant === "primary"
      ? COLOR.primary
      : $variant === "danger"
      ? "#fecdd3"
      : $variant === "disabled"
      ? "#e5e7eb"
      : "#bfd3ea"};
  &:disabled {
    opacity: 0.75;
    cursor: not-allowed;
  }
`;

const Empty = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  grid-column: 1 / -1;
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  border: 1px dashed #cbd5f5;
`;
const EmptySmall = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 10px;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1000;
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
  display: grid;
  gap: 12px;
`;
const ModalHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const ModalTitle = styled.div`
  font-weight: 900;
  color: ${COLOR.text};
  font-size: 1.1rem;
`;
const ModalSub = styled.div`
  color: ${COLOR.muted};
  font-size: 0.9rem;
`;
const CloseBtn = styled.button`
  border: none;
  background: #f1f5f9;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
`;

const AssignList = styled.div`
  max-height: 280px;
  overflow: auto;
  display: grid;
  gap: 8px;
  padding-right: 2px;
`;
const AssignRow = styled.button`
  border: 1px solid ${({ $active }) => ($active ? COLOR.primary : COLOR.line)};
  background: ${({ $active, $disabled }) => ($disabled ? "#f8fafc" : $active ? "var(--app-primary-soft)" : "#fff")};
  color: ${({ $disabled }) => ($disabled ? COLOR.muted : COLOR.text)};
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;
const SmallPill = styled.span`
  border: 1px solid ${COLOR.line};
  background: #fff7ed;
  color: ${COLOR.yellow};
  border-radius: 999px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 700;
`;

const PreviewCard = styled.div`
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
`;
const PreviewTop = styled.div`
  padding: 18px 16px 14px;
  text-align: center;
`;
const PreviewTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #111827;
`;
const QrBox = styled.div`
  margin: 12px auto 0;
  width: 220px;
  height: 220px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  display: grid;
  place-items: center;
  background: #fff;
  img {
    width: 200px;
    height: 200px;
  }
`;
const CodePill = styled.div`
  margin: 12px auto 0;
  padding: 8px 14px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  font-weight: 800;
  letter-spacing: 1px;
  color: #111827;
  display: inline-block;
`;
const PreviewSub = styled.div`
  margin-top: 8px;
  color: #4b5563;
  font-size: 12px;
`;
const PreviewBottom = styled.div`
  background: ${COLOR.primary};
  padding: 12px 10px;
  display: grid;
  place-items: center;
  gap: 6px;
  img {
    width: 120px;
    height: auto;
    object-fit: contain;
  }
  span {
    color: #ffffff;
    font-size: 11px;
  }
`;
const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
`;
const Input = styled.input`
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  padding: 10px 12px;
  outline: none;
`;

const Centered = styled.div`
  min-height: 40vh;
  display: grid;
  place-items: center;
  gap: 8px;
  color: ${COLOR.muted};
`;
const ActivityIndicatorCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid #e5e7eb;
  border-top-color: ${COLOR.primary};
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
